/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { tokens } from '@neo4j-ndl/base';
import classNames from 'classnames';
import type React from 'react';
import { useMemo, useState } from 'react';

import {
  type HtmlAttributes,
  type PolymorphicCommonProps,
} from '../_common/types';
import {
  getDisabledTextColor,
  getHoverColor,
  getTextColorFromBackground,
} from './color';

/**
 *
 *
 * Types
 *
 *
 */

type GraphLabelProps = {
  /**  The type of label */
  type:
    | 'node'
    | 'relationship'
    | 'propertyKey'
    | 'relationshipLeft'
    | 'relationshipRight';
  /** Callback function that is called when the label is clicked */
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /** Additional styles to apply to the label */
  style?: React.CSSProperties;
  /** Content displayed inside the label */
  children?: React.ReactNode;
  /** The background color of the label */
  color?: string;
  /** Whether the label is disabled */
  isDisabled?: boolean;
  /** Additional class names */
  className?: string;
  /** Whether the label is selected */
  isSelected?: boolean;
  /** Whether the label should expand to full available width */
  isFluid?: boolean;
  /** The size of the label */
  size?: 'small' | 'large';
};

type HexagonEndProps = {
  /** The direction of the hexagon end */
  direction: 'left' | 'right';
  /** The color of the hexagon end */
  color?: string;
  /** The height of the hexagon end in pixels */
  height?: number;
  /** HTML attributes */
  htmlAttributes?: HtmlAttributes<'div'>;
};
/**
 *
 *
 * Helpers
 *
 *
 */

const HexagonEnd = ({
  direction = 'left',
  color,
  htmlAttributes,
  height = 24,
}: HexagonEndProps) => {
  const classes = classNames('ndl-hexagon-end', {
    'ndl-left': direction === 'left',
    'ndl-right': direction === 'right',
  });

  return (
    <div className={classes} {...htmlAttributes}>
      <svg
        aria-hidden={true}
        className="ndl-hexagon-end-inner"
        fill="none"
        height={height}
        preserveAspectRatio="none"
        viewBox="0 0 9 24"
        width="9"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* triangle */}
        <path
          style={{ fill: color }}
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.73024 1.03676C6.08165 0.397331 6.75338 0 7.48301 0H9V24H7.483C6.75338 24 6.08165 23.6027 5.73024 22.9632L0.315027 13.1094C-0.105009 12.4376 -0.105009 11.5624 0.315026 10.8906L5.73024 1.03676Z"
        />
      </svg>
    </div>
  );
};

type SquareEndProps = {
  /** The direction of the square end (where it's rounded) */
  direction: 'left' | 'right';
  /** The color of the square end */
  color: string;
  /** HTML attributes */
  htmlAttributes?: HtmlAttributes<'div'>;
};

const SquareEnd = ({
  direction = 'left',
  color,
  htmlAttributes,
}: SquareEndProps) => {
  const classes = classNames('ndl-square-end', {
    'ndl-left': direction === 'left',
    'ndl-right': direction === 'right',
  });

  return (
    <div className={classes} {...htmlAttributes}>
      <div
        className="ndl-square-end-inner"
        style={{ backgroundColor: color }}
      />
    </div>
  );
};

/**
 *
 *
 * GraphLabel Component
 *
 *
 */

const MAX_WIDTH = 200;

export const GraphLabel = <T extends React.ElementType = 'button'>({
  type = 'node',
  color,
  isDisabled = false,
  isSelected = false,
  as,
  onClick,
  className,
  style,
  children,
  htmlAttributes,
  isFluid = false,
  size = 'large',
  ref,
  ...restProps
}: PolymorphicCommonProps<T, GraphLabelProps>) => {
  const [isHover, setIsHover] = useState(false);

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setIsHover(true);

    if (htmlAttributes && htmlAttributes.onMouseEnter !== undefined) {
      htmlAttributes.onMouseEnter(event);
    }
  };

  const handleMouseLeave = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setIsHover(false);

    htmlAttributes?.onMouseLeave?.(event);
  };

  const Component: React.ElementType = as ?? 'button';
  const isButton = Component === 'button';

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    // By default, a <button /> element with disabled set to true will not get its
    // on click handler called. To support the same behavior on <a /> tags we
    // swallow the event here when disabled, since the <a /> tag with disabled set to
    // true will have its onClick handler called
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (onClick) {
      onClick(event);
    }
  };

  let backgroundColor = useMemo(() => {
    if (color === undefined) {
      switch (type) {
        case 'node':
          return tokens.graph['1'];
        case 'relationship':
        case 'relationshipLeft':
        case 'relationshipRight':
          return tokens.theme.light.color.neutral.bg.strong;
        default:
          return tokens.theme.light.color.neutral.bg.strongest;
      }
    }
    return color;
  }, [color, type]);

  const hoverColor = useMemo(
    () =>
      getHoverColor(
        backgroundColor ? backgroundColor : tokens.palette.lemon[40],
      ),
    [backgroundColor],
  );
  const textColor = useMemo(
    () =>
      getTextColorFromBackground(
        backgroundColor ? backgroundColor : tokens.palette.lemon[40],
      ),
    [backgroundColor],
  );
  const disabledTextColor = useMemo(
    () =>
      getDisabledTextColor(
        backgroundColor ? backgroundColor : tokens.palette.lemon[40],
      ),
    [backgroundColor],
  );

  if (isHover && !isDisabled) {
    backgroundColor = hoverColor;
  }

  const commonClasses = classNames('ndl-graph-label', className, {
    'ndl-disabled': isDisabled,
    'ndl-interactable': isButton,
    'ndl-selected': isSelected,
    'ndl-small': size === 'small',
  });

  if (type === 'node') {
    const classes = classNames('ndl-node-label', commonClasses);

    return (
      <Component
        className={classes}
        ref={ref}
        style={{
          backgroundColor: backgroundColor,
          color: isDisabled ? disabledTextColor : textColor,
          maxWidth: isFluid ? '100%' : MAX_WIDTH,
          ...style,
        }}
        {...(isButton && {
          disabled: isDisabled,
          onClick: handleClick,
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
          type: 'button',
        })}
        {...htmlAttributes}
      >
        <div className="ndl-node-label-content">{children}</div>
      </Component>
    );
  } else if (
    type === 'relationship' ||
    type === 'relationshipLeft' ||
    type === 'relationshipRight'
  ) {
    const classes = classNames('ndl-relationship-label', commonClasses);
    const height = size === 'small' ? 20 : 24;
    const focusRingClasses = classNames('ndl-relationship-focus-ring', {
      'ndl-end-left-square': type === 'relationshipRight',
      'ndl-end-right-square': type === 'relationshipLeft',
    });

    return (
      <Component
        style={{
          maxWidth: isFluid ? '100%' : MAX_WIDTH,
          ...style,
          color: isDisabled ? disabledTextColor : textColor,
        }}
        className={classes}
        {...(isButton && {
          disabled: isDisabled,
          onClick: handleClick,
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
          type: 'button',
        })}
        ref={ref}
        {...restProps}
        {...htmlAttributes}
      >
        {type === 'relationshipLeft' || type === 'relationship' ? (
          <HexagonEnd
            direction="left"
            color={backgroundColor}
            height={height}
          />
        ) : (
          <SquareEnd direction="left" color={backgroundColor} />
        )}
        <div
          className="ndl-relationship-label-container"
          style={{
            backgroundColor: backgroundColor,
          }}
        >
          <div className="ndl-relationship-label-content">{children}</div>
        </div>
        {type === 'relationshipRight' || type === 'relationship' ? (
          <HexagonEnd
            direction="right"
            color={backgroundColor}
            height={height}
          />
        ) : (
          <SquareEnd direction="right" color={backgroundColor} />
        )}
        <span aria-hidden={true} className={focusRingClasses} />
      </Component>
    );
  } else {
    const classes = classNames('ndl-property-key-label', commonClasses);

    return (
      <Component
        {...(isButton && {
          type: 'button',
        })}
        style={{
          backgroundColor: backgroundColor,
          color: isDisabled ? disabledTextColor : textColor,
          maxWidth: isFluid ? '100%' : MAX_WIDTH,
          ...style,
        }}
        className={classes}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={ref}
        {...htmlAttributes}
      >
        <div className="ndl-property-key-label-content">{children}</div>
      </Component>
    );
  }
};
